/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren } from 'preact';
import { MutableRef, useCallback } from 'preact/hooks';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { Floating } from '../UNSAFE_Floating';
import { Layer } from '../UNSAFE_Layer';
import { isControlOrFunctionKey, KEYS, preventDefaultForCurrentTarget } from './eventUtils';
import { useOutsideMousedown } from './useOutsideMousedown';

const rootStyles = {
  base: cssProps`${{
    boxSizing: 'border-box',
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-popup-bg-color)',
    border: '1px solid var(--oj-c-PRIVATE-DO-NOT-USE-popup-border-color)',
    borderRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-md)',
    boxShadow: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-dropdown-box-shadow)',

    display: 'flex',
    flexDirection: 'column',

    maxHeight: '400px'
  }}`
};

type Props = {
  anchorRef: MutableRef<HTMLElement | null>;
  children?: ComponentChildren;
  dropdownRef: MutableRef<HTMLElement | null>;
  id?: string;
  isOpen?: boolean;
  onAutoDismiss?: (event?: Event) => void;
};

export function Dropdown({ anchorRef, children, dropdownRef, id, isOpen, onAutoDismiss }: Props) {
  const handleAutoDismiss = useCallback(
    (event: Event) => {
      onAutoDismiss?.(event);
    },
    [onAutoDismiss]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.defaultPrevented || isControlOrFunctionKey(event)) {
        return;
      }

      switch (event.code) {
        case KEYS.ESC:
        case KEYS.TAB:
          handleAutoDismiss(event);
          break;
      }
    },
    [handleAutoDismiss]
  );

  // use the mousedown event to be consistent with legacy JET and so that we're hiding the dropdown
  // on the same event where focus will transfer
  useOutsideMousedown({
    isDisabled: !isOpen,
    ref: [anchorRef, dropdownRef],
    handler: handleAutoDismiss
  });

  // TODO: get dropdown y (mainAxis) offset from --oj-c-PRIVATE-DO-NOT-USE-private-core-global-dropdown-offset
  const offsetValue = { mainAxis: 4, crossAxis: 0 };

  const inlineStyle = {
    minWidth: `${anchorRef.current?.offsetWidth}px`
  };

  return !isOpen ? null : (
    <Layer logicalParentRef={anchorRef}>
      <Floating
        anchorRef={anchorRef}
        ref={dropdownRef}
        placement="bottom-start"
        offsetValue={offsetValue}>
        <div
          class={rootStyles.base}
          id={id}
          style={inlineStyle}
          onKeyDown={handleKeyDown}
          onMouseDown={preventDefaultForCurrentTarget}>
          {children}
        </div>
      </Floating>
    </Layer>
  );
}
